/**
 * Skeleton Loader Component
 * 
 * Animated loading placeholders for better perceived performance
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './SkeletonLoader.css'

interface SkeletonProps {
  width?: string | number
  height?: string | number
  borderRadius?: string | number
  className?: string
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  borderRadius = '4px',
  className = ''
}) => {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius
      }}
    />
  )
}

interface SkeletonTextProps {
  lines?: number
  width?: string
  lastLineWidth?: string
  className?: string
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  width = '100%',
  lastLineWidth = '60%',
  className = ''
}) => {
  return (
    <div className={`skeleton-text ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          width={i === lines - 1 ? lastLineWidth : width} 
          height={14}
        />
      ))}
    </div>
  )
}

interface SkeletonCardProps {
  className?: string
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ className = '' }) => {
  return (
    <div className={`skeleton-card ${className}`}>
      <div className="skeleton-card-header">
        <Skeleton width={40} height={40} borderRadius="50%" />
        <div className="skeleton-card-header-text">
          <Skeleton width="60%" height={14} />
          <Skeleton width="40%" height={12} />
        </div>
      </div>
      <SkeletonText lines={2} />
      <div className="skeleton-card-footer">
        <Skeleton width={80} height={32} borderRadius={8} />
        <Skeleton width={80} height={32} borderRadius={8} />
      </div>
    </div>
  )
}

interface SkeletonTableRowProps {
  columns?: number
}

export const SkeletonTableRow: React.FC<SkeletonTableRowProps> = ({ columns = 5 }) => {
  return (
    <div className="skeleton-table-row">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} width={`${100 / columns}%`} height={16} />
      ))}
    </div>
  )
}

interface SkeletonTableProps {
  rows?: number
  columns?: number
  className?: string
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({ 
  rows = 5, 
  columns = 5,
  className = ''
}) => {
  return (
    <div className={`skeleton-table ${className}`}>
      {/* Header */}
      <div className="skeleton-table-header">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height={14} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <SkeletonTableRow key={rowIndex} columns={columns} />
      ))}
    </div>
  )
}

interface SkeletonStatsProps {
  count?: number
}

export const SkeletonStats: React.FC<SkeletonStatsProps> = ({ count = 4 }) => {
  return (
    <div className="skeleton-stats">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-stat-card">
          <Skeleton width={32} height={32} borderRadius={8} />
          <Skeleton width={60} height={24} />
          <Skeleton width={80} height={12} />
        </div>
      ))}
    </div>
  )
}

export default Skeleton